import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { INDIAN_STATES } from "@/lib/states";
import { toast } from "sonner";
import { User, Mail, Lock, Phone, ArrowRight, Sparkles, Briefcase, MapPin } from "lucide-react";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") === "provider" ? "provider" : "customer";

  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", state: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ ...form, role, state: form.state || undefined });
      toast.success(`Welcome, ${u.name.split(" ")[0]}!${u.code ? ` Your ID: ${u.code}` : ""}`);
      if (u.role === "provider") {
        toast.info("Complete KYC to start listing your services.");
        nav("/provider/kyc");
      } else {
        nav("/browse");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-2" data-testid="register-page">
      <div className="hidden overflow-hidden rounded-3xl border bg-brand-ink text-white lg:block">
        <div className="relative h-full p-10">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-orange"><Sparkles size={18} /></div>
          <h2 className="mt-8 font-heading text-4xl font-extrabold leading-tight tracking-tight">
            Join <span className="text-brand-cyan">TridentDigitalPulse.</span>
          </h2>
          <p className="mt-4 max-w-sm text-white/70">
            Get things done at home, or grow your local business — all in one place.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/80">
            <li>• Verified providers with KYC</li>
            <li>• Instant UPI booking confirmation</li>
            <li>• Ratings you can trust</li>
          </ul>
        </div>
      </div>

      <div>
        <div className="rounded-3xl border border-foreground/10 bg-white p-6 sm:p-10">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Create account</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Already have one? <Link to="/login" className="font-semibold text-brand-orange">Log in</Link>
          </p>

          <div className="mt-6 grid grid-cols-2 gap-2 rounded-full border border-foreground/10 bg-muted p-1" data-testid="role-toggle">
            <button
              type="button"
              onClick={() => setRole("customer")}
              data-testid="role-customer"
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${role === "customer" ? "bg-brand-ink text-white" : "text-foreground/70"}`}
            >
              <User size={14} className="mr-1.5 inline-block" /> Customer
            </button>
            <button
              type="button"
              onClick={() => setRole("provider")}
              data-testid="role-provider"
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${role === "provider" ? "bg-brand-ink text-white" : "text-foreground/70"}`}
            >
              <Briefcase size={14} className="mr-1.5 inline-block" /> Provider
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full name</Label>
              <div className="relative">
                <User size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="name" required value={form.name} onChange={set("name")} className="pl-9 h-11" data-testid="reg-name" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} className="pl-9 h-11" data-testid="reg-email" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <div className="relative">
                  <Phone size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="pl-9 h-11" data-testid="reg-phone" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="pl-9 h-11" data-testid="reg-password" />
              </div>
              <p className="text-[11px] text-muted-foreground">At least 6 characters</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="state" className="flex items-center gap-1"><MapPin size={12} /> State</Label>
              <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                <SelectTrigger id="state" data-testid="reg-state"><SelectValue placeholder="Select your state" /></SelectTrigger>
                <SelectContent>
                  {INDIAN_STATES.map((s) => (
                    <SelectItem key={s.code} value={s.name}>{s.name} ({s.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Used to generate your unique account ID.</p>
            </div>

            <Button
              type="submit" disabled={loading}
              className="h-11 w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
              data-testid="reg-submit"
            >
              {loading ? "Creating..." : (<>Create account <ArrowRight size={16} /></>)}
            </Button>

            {role === "provider" && (
              <p className="rounded-xl bg-muted p-3 text-xs text-muted-foreground">
                As a provider you'll need to upload KYC docs (Aadhaar / Driving Licence / Voter ID)
                before your listings go live.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
